<template>
  <card class="card">
    <h4 class="text-center">Bookie Match Betability</h4>

    <form class="row mb-3" @submit.prevent="addUrl">
      <div class="col-md-9">
        <input
          v-model.trim="url"
          type="url"
          class="form-control"
          placeholder="Paste a supported bookie match URL (currently Pepeta)"
          required
        />
      </div>
      <div class="col-md-3">
        <button type="submit" class="btn btn-primary btn-block" :disabled="isSaving">
          {{ isSaving ? 'Adding...' : 'Track Match' }}
        </button>
      </div>
    </form>

    <div class="row">
      <div class="col-md-6 text-left">
        <a href="#" @click.prevent="getTrackedUrls">
          <em
            class="tim-icons icon-refresh-02"
            :class="{ rotating: isLoading }"
            style="font-size: 22px; margin-bottom: 10px"
          ></em>
        </a>
      </div>
      <div class="col-md-6 text-right">
        <select v-model="status" class="form-control d-inline-block w-auto" @change="getTrackedUrls">
          <option value="bettable">Still bettable</option>
          <option value="closed">Betting closed</option>
          <option value="all">All matches</option>
        </select>
      </div>
    </div>

    <vue-good-table
      :columns="columns"
      :rows="trackedUrls"
      theme="nocturnal"
      :search-options="{ enabled: true }"
      :pagination-options="{ enabled: true, perPage: 10, allLabel: 'All' }"
    >
      <div slot="emptystate">
        <span v-show="isLoading">Loading...</span>
        <span v-show="!isLoading">There are no tracked bookie matches.</span>
      </div>

      <template slot="table-row" slot-scope="props">
        <a
          v-if="props.column.field === 'url'"
          :href="props.row.url"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open match
        </a>
        <span v-else-if="props.column.field === 'bookie'" class="text-capitalize">
          {{ props.row.bookie }}
        </span>
        <span v-else-if="props.column.field === 'match_name'">
          {{ props.row.match_name || `Match #${props.row.match_id}` }}
        </span>
        <span v-else-if="props.column.field === 'is_bettable'">
          <span :class="props.row.is_bettable ? 'text-success' : 'text-danger'">
            {{ props.row.is_bettable ? 'Bettable' : 'Closed' }}
          </span>
        </span>
        <span v-else-if="props.column.field === 'last_checked_at'">
          {{ props.row.last_checked_at ? formatDateTime(props.row.last_checked_at) : 'Awaiting check' }}
        </span>
        <span v-else-if="props.column.field === 'created_at'">
          {{ formatDateTime(props.row.created_at) }}
        </span>
        <span v-else-if="props.column.field === 'actions'">
          <base-button icon round @click="deleteUrl(props.row.id)">
            <i class="tim-icons icon-trash-simple"></i>
          </base-button>
        </span>
        <span v-else>{{ props.formattedRow[props.column.field] }}</span>
      </template>
    </vue-good-table>
  </card>
</template>

<script>
import axios from 'axios';
import formatDateTime from '@/mixin/formatDateTime';

export default {
  name: 'BookieTrackedUrls',
  mixins: [formatDateTime],
  data() {
    return {
      url: '',
      status: 'bettable',
      trackedUrls: [],
      isLoading: false,
      isSaving: false,
      columns: [
        { label: 'Bookie', field: 'bookie' },
        { label: 'Match', field: 'match_name' },
        { label: 'Status', field: 'is_bettable' },
        { label: 'URL', field: 'url', sortable: false },
        { label: 'Last checked', field: 'last_checked_at' },
        { label: 'Created at', field: 'created_at' },
        { label: 'Actions', field: 'actions', sortable: false, width: '120px' }
      ]
    };
  },
  mounted() {
    this.getTrackedUrls();
  },
  methods: {
    async getTrackedUrls() {
      this.isLoading = true;
      try {
        const response = await axios.get('/bookie-tracked-urls', {
          params: { status: this.status }
        });
        this.trackedUrls = response.data;
      } catch (error) {
        console.error('Error fetching tracked bookie matches:', error);
        this.$toast.error('Could not load tracked bookie matches');
      } finally {
        this.isLoading = false;
      }
    },
    async addUrl() {
      this.isSaving = true;
      try {
        const response = await axios.post('/bookie-tracked-urls', { url: this.url });
        this.url = '';
        if (!response.data.is_bettable) {
          this.status = 'closed';
        }
        this.$toast.success(
          response.data.is_bettable
            ? 'Bookie match is being tracked'
            : 'Betting is already closed for this match'
        );
        await this.getTrackedUrls();
      } catch (error) {
        const errors = error.response && error.response.data && error.response.data.errors;
        const message = errors && errors.url
          ? errors.url[0]
          : error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Could not add bookie match URL';
        this.$toast.error(message);
      } finally {
        this.isSaving = false;
      }
    },
    async deleteUrl(id) {
      try {
        await axios.delete(`/bookie-tracked-urls/${id}`);
        this.$toast.success('Bookie match URL removed');
        await this.getTrackedUrls();
      } catch (error) {
        console.error('Error deleting tracked bookie match:', error);
        this.$toast.error('Could not remove bookie match URL');
      }
    }
  }
};
</script>
